import {
  type ColumnFiltersState,
  createColumnHelper,
  type ExpandedState,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format, isValid, parseISO, subMonths } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { ChevronDown, ChevronRight, EllipsisVertical } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type DateRange } from 'react-day-picker';

import {
  type PaymentHistorySortOrder,
  STUDENT_PAYMENT_HISTORY_DATE_FIELD,
  type StudentPaymentHistoryDateField,
  type StudentPaymentHistoryInvoice,
  useGetStudentPaymentHistory,
} from '~entities/student/api/get-student-payment-history';
import { useCurrentStudio } from '~entities/studios/model/studio-store';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { DateRangePicker } from '~features/payment/ui/date-range-picker';
import { RefundDialog } from '~features/students/ui/student-payment/refund-dialog';
import { ScheduledFailedInvoiceAction } from '~features/students/ui/student-payment/scheduled-failed-invoice-action';

import { useGetCurrency } from '~shared/api/currency-api';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { parseToNoonDate } from '~shared/helpers/datetime';
import { normalizePayments } from '~shared/helpers/normalize-payments';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { cn } from '~shared/shadcn/lib/utils';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

import { RetryOrCollectPayment } from './retry-or-collect-payment';
import {
  formatPaymentHistoryProductLabel,
  resolvePaymentHistoryRefundAction,
} from './student-payment-history-actions.helpers';

// import { Button } from '~shared/shadcn/ui/button';

const columnHelper = createColumnHelper<StudentPaymentHistoryInvoice>();

function getDefaultTransactionHistoryDateRange(): DateRange {
  const now = new Date();
  return {
    from: subMonths(now, 3),
    to: now,
  };
}

interface StudentPaymentHistoryTableProps {
  studentId: string;
}

const manualPaymentMethods = new Set(['cash', 'check']);

const getNormalizedPaymentMethod = (payment: { method?: string | null }) =>
  payment.method?.toLowerCase() ?? '';

const getInvoiceLabel = (
  invoiceId: string | null | undefined,
  className: string | null | undefined,
) => {
  const trimmedInvoiceId = invoiceId?.trim();
  if (trimmedInvoiceId === undefined || trimmedInvoiceId.length === 0) {
    return formatPaymentHistoryProductLabel(className);
  }
  return trimmedInvoiceId;
};

const hasManualCashCheckPayment = (
  payments: Array<{ method?: string }>,
  paymentMethod?: string,
) =>
  manualPaymentMethods.has(paymentMethod?.toLowerCase() ?? '') ||
  payments.some((payment) =>
    manualPaymentMethods.has(payment.method?.toLowerCase() ?? ''),
  );

const getPaymentsTotal = (payments: StudentPaymentHistoryInvoice['payments']) =>
  normalizePayments(payments).reduce(
    (sum, payment) => sum + Number(payment.amount),
    0,
  );

const getInvoicePaidAmount = (invoice: StudentPaymentHistoryInvoice) => {
  const paymentsTotal = getPaymentsTotal(invoice.payments);

  if (paymentsTotal <= 0) {
    return Number(invoice.paidAmount);
  }

  return paymentsTotal;
};

const refundStatuses = new Set([
  'full refund',
  'partial refund',
  'pending refund',
  'refund failed',
]);

const isRefundStatus = (status: string) => refundStatuses.has(status);

const formatPaymentType = (method: string) => {
  if (method === 'cash') return 'Manual - Cash';
  if (method === 'check') return 'Manual - Check';
  return method.charAt(0).toUpperCase() + method.slice(1);
};

const getParentPaymentTypes = (
  payments: StudentPaymentHistoryInvoice['payments'],
  paymentMethod?: string,
) => {
  const normalizedPayments = normalizePayments(payments);
  const hasManualPayment = hasManualCashCheckPayment(
    normalizedPayments,
    paymentMethod,
  );
  const methodsFromPayments = normalizedPayments
    .map(getNormalizedPaymentMethod)
    .filter((method) => method && !manualPaymentMethods.has(method));

  // Prefer how it was actually paid. Only use invoice.paymentMethod when
  // payments[] is empty (legacy rows). Otherwise full-wallet enrolls that
  // still have a card on file would show "Wallet, Card".
  if (
    methodsFromPayments.length === 0 &&
    paymentMethod &&
    !manualPaymentMethods.has(paymentMethod.toLowerCase())
  ) {
    methodsFromPayments.push(paymentMethod.toLowerCase());
  }

  const paymentTypes = [
    ...new Set(methodsFromPayments.map(formatPaymentType).filter(Boolean)),
  ];

  return hasManualPayment ? ['Manual', ...paymentTypes] : paymentTypes;
};

const formatPaymentMethodLabel = (method: string) => {
  if (method === 'cash') return 'Cash';
  if (method === 'check') return 'Check';
  if (method === 'card') return 'Card';
  if (method === 'wallet') return 'Wallet';
  if (method === 'us_bank_account') return 'ACH Transfer';
  if (method === 'au_becs_debit') return 'BECS';
  if (method === 'link') return 'Link';
  return method;
};

const getManualPayments = (
  payments: StudentPaymentHistoryInvoice['payments'],
) =>
  normalizePayments(payments).filter((payment) =>
    manualPaymentMethods.has(getNormalizedPaymentMethod(payment)),
  );

export const StudentPaymentHistoryTable = ({
  studentId,
}: StudentPaymentHistoryTableProps) => {
  const { data: currencyDetails } = useGetCurrency();
  const { hasAdminAccess } = useCurrentUserRole();
  const currentStudio = useCurrentStudio();
  const studioTimezone = currentStudio?.timezone;

  const formatDateForDisplay = useCallback(
    (iso: string, fmt: string) => {
      try {
        if (studioTimezone) {
          return formatInTimeZone(iso, studioTimezone, fmt);
        }
        // Fallback to previous behavior (browser timezone)
        return format(parseISO(iso), fmt);
      } catch {
        // Never render blank dates; fall back to previous behavior if timezone parsing fails.
        try {
          return format(parseISO(iso), fmt);
        } catch {
          return '';
        }
      }
    },
    [studioTimezone],
  );

  /** Date-only due dates are UTC midnight ISO — show that calendar day (match Upcoming Payments / ENR-2139). */
  const formatDueDateForDisplay = useCallback((iso: string) => {
    const atNoon = parseToNoonDate(iso);
    if (atNoon == null || !isValid(atNoon)) return '';
    return format(atNoon, 'MMM, dd, yyyy');
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'expand',
        header: () => <div className='w-8' />,
        cell: (info) => {
          const manualPayments = getManualPayments(info.row.original.payments);
          if (manualPayments.length === 0) {
            return <div className='w-8' />;
          }

          return (
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='size-8'
              onClick={() => {
                info.row.toggleExpanded();
              }}
              aria-label={
                info.row.getIsExpanded()
                  ? 'Hide manual payment details'
                  : 'Show manual payment details'
              }
            >
              {info.row.getIsExpanded() ? (
                <ChevronDown className='size-4' />
              ) : (
                <ChevronRight className='size-4' />
              )}
            </Button>
          );
        },
      }),
      columnHelper.accessor('dueDate', {
        header: (info) => (
          <DataTableColumnHeader
            className='w-36 pl-4 text-black'
            column={info.column}
            title='Due Date'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='px-4 py-2'>
              {value ? formatDueDateForDisplay(value) : ''}
            </div>
          );
        },
      }),
      columnHelper.accessor('paymentDate', {
        header: (info) => (
          <DataTableColumnHeader
            className='w-36 pl-4 text-black'
            column={info.column}
            title='Payment Date'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='px-4 py-2'>
              {value ? formatDateForDisplay(value, 'MMM, dd, yyyy') : ''}
            </div>
          );
        },
      }),

      columnHelper.accessor('_id', {
        id: 'invoiceReference',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Invoice Ref'
            disableSorting
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2 font-mono text-xs text-muted-foreground'>
            {info.getValue()}
          </div>
        ),
      }),

      columnHelper.accessor('className', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Product'
            disableSorting
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>
            {formatPaymentHistoryProductLabel(info.getValue())}
          </div>
        ),
      }),
      columnHelper.accessor('status', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Status'
            disableSorting
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>
            <Badge
              className={cn({
                'bg-[#D4EDDA]': info.getValue() === 'paid',
                'bg-[#F8D7DA] text-[#721C24]':
                  info.getValue() === 'failed' || info.getValue() === 'invalid',
                'bg-[#FFF3CD]':
                  info.getValue() === 'scheduled' ||
                  info.getValue() === 'invoice scheduled' ||
                  info.getValue() === 'pending',
                'bg-[#D6D6D6]': info.getValue() === 'cancelled',
              })}
              variant={'default'}
            >
              {info.getValue()}
            </Badge>
          </div>
        ),
      }),
      columnHelper.accessor('payments', {
        id: 'paymentType',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Payment Type '
            disableSorting
          />
        ),
        cell: (info) => {
          const payments = normalizePayments(info.getValue());
          const displayPaymentTypes = getParentPaymentTypes(
            payments,
            info.row.original.paymentMethod,
          );

          return (
            <div className='px-4 py-2'>
              {displayPaymentTypes.length > 0
                ? displayPaymentTypes.join(' + ')
                : '—'}
            </div>
          );
        },
      }),
      columnHelper.accessor('payments', {
        id: 'recordedBy',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Recorded By'
            disableSorting
          />
        ),
        cell: (info) => {
          const payments = normalizePayments(info.getValue());
          const recordedBy = [
            ...new Set(
              payments
                .map((payment) => payment.recordedByEmail ?? payment.recordedBy)
                .filter(Boolean),
            ),
          ];

          return (
            <div className='px-4 py-2'>
              {recordedBy.length > 0 ? recordedBy.join(', ') : '—'}
            </div>
          );
        },
      }),
      // "Amount" should reflect the invoice total actually owed/charged (finalAmount),
      // not the pre-tax/pre-fee baseAmount.
      columnHelper.accessor('finalAmount', {
        id: 'amount',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Total Invoice'
            disableSorting
          />
        ),
        cell: (info) => {
          const amount = Number(info.getValue());

          return (
            <div className='px-4 py-2 font-medium'>
              {currencyDetails?.symbol}
              {amount.toFixed(2)}
            </div>
          );
        },
      }),
      columnHelper.accessor('transactionFeeAmount', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Transaction Fee'
            disableSorting
          />
        ),
        cell: (info) => {
          const amount = Number(info.getValue() ?? 0);
          return amount > 0 ? (
            <div className='px-4 py-2 font-medium'>
              {currencyDetails?.symbol}
              {amount.toFixed(2)}
            </div>
          ) : (
            <div className='px-4 py-2'>—</div>
          );
        },
      }),
      columnHelper.accessor('discount', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Discount'
            disableSorting
          />
        ),
        cell: (info) =>
          Number(info.getValue()) > 0 ? (
            <div className='text-nowrap px-4 py-2 font-medium text-green-600'>
              - {currencyDetails?.symbol}
              {info.getValue()}
            </div>
          ) : (
            '' // <div className='ml-9'>-</div>
          ),
      }),
      columnHelper.accessor('paidAmount', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Paid'
            disableSorting
          />
        ),
        cell: (info) => {
          const invoice = info.row.original;
          const paymentsTotal = getInvoicePaidAmount(invoice);
          const apiPaidAmount = Number(info.getValue());
          const paidAmount =
            paymentsTotal > 0 && !isRefundStatus(invoice.status)
              ? paymentsTotal
              : apiPaidAmount;

          return (
            <div className='px-4 py-2 font-medium'>
              {currencyDetails?.symbol}
              {paidAmount.toFixed(2)}
            </div>
          );
        },
      }),
      /*columnHelper.accessor(
        (row) =>
          Math.max(0, Number(row.finalAmount || 0) - Number(row.paidAmount || 0)),
        {
          id: 'outstanding',
          header: (info) => (
            <DataTableColumnHeader
              className='pl-4 text-black'
              column={info.column}
              title='Outstanding'
            />
          ),
          cell: (info) => (
            <div className='px-4 py-2 font-medium'>
              {currencyDetails?.symbol}
              {Number(info.getValue() || 0).toFixed(2)}
            </div>
          ),
        },
      ),*/
      columnHelper.display({
        id: 'note',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Note'
            disableSorting
          />
        ),
        cell: (info) => {
          const invoice = info.row.original;
          const hasManualPayments =
            getManualPayments(invoice.payments).length > 0;
          if (hasManualPayments) {
            return <div className='px-4 py-2 font-medium' />;
          }

          const primaryNote =
            invoice.refundReason ?? invoice.metadata?.description ?? null;
          const failureReason = invoice.failureReason?.trim()
            ? invoice.failureReason
            : null;
          const hasPrimary = Boolean(primaryNote?.trim());

          if (!hasPrimary && !failureReason) {
            return <div className='px-4 py-2 font-medium' />;
          }

          return (
            <div className='space-y-2 px-4 py-2 font-medium'>
              {primaryNote ? <div>{primaryNote}</div> : null}
              {failureReason ? (
                <div className='rounded-md border border-red-500 bg-red-50 p-1 text-sm text-red-500'>
                  Failure reason: {failureReason}
                </div>
              ) : null}
            </div>
          );
        },
      }),
      columnHelper.accessor('payments', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Transaction ID'
            disableSorting
          />
        ),
        cell: (info) => {
          const payments = normalizePayments(info.getValue());
          const paymentIntentIds = payments
            .filter(
              (payment) =>
                !manualPaymentMethods.has(getNormalizedPaymentMethod(payment)),
            )
            .map((payment) => payment.paymentIntentId)
            .filter(Boolean);
          const trimmedInvoiceId = info.row.original.invoiceId?.trim();
          const fallbackTransactionId =
            trimmedInvoiceId === '' ? undefined : trimmedInvoiceId;
          const displayTransactionId =
            paymentIntentIds[0] ?? fallbackTransactionId ?? '—';

          return (
            <div className='px-4 py-2 font-mono text-xs text-muted-foreground'>
              <Tooltip>
                <TooltipTrigger>{displayTransactionId}</TooltipTrigger>
                <TooltipContent>
                  <p>
                    {paymentIntentIds.length > 0
                      ? paymentIntentIds.join(', ')
                      : (fallbackTransactionId ?? '—')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Actions'
          />
        ),
        cell: (info) => {
          if (!hasAdminAccess) {
            return '';
          }
          if (info.row.original.isLineItemRow) {
            return '';
          }
          const refundAction = resolvePaymentHistoryRefundAction(
            info.row.original,
          );

          if (refundAction.type === 'reattempt') {
            return (
              <RetryOrCollectPayment
                key={`retry-or-collect-payment-${info.row.original._id}`}
                invoiceId={info.row.original._id}
                invoiceDate={info.row.original.invoiceDate}
                buttonText='Reattempt Charge'
              />
            );
          }

          if (refundAction.type === 'scheduled-failed') {
            return (
              <ScheduledFailedInvoiceAction
                key={`scheduled-failed-invoice-action-${info.row.original._id}`}
                transactionId={info.row.original._id}
                internalTransactionId={
                  info.row.original.internalTransactionId ?? ''
                }
                invoiceDate={info.row.original.invoiceDate}
                status={info.row.original.status}
              />
            );
          }

          if (refundAction.type !== 'refund') {
            return '';
          }

          const { manualAmount, onlineAmount, onlineMode } = refundAction;
          // Cash/check reverse lives in the Manual payment ledger expand.
          const showOnlineReversal = onlineAmount > 0;
          const invoiceLabel = getInvoiceLabel(
            info.row.original.invoiceId,
            info.row.original.className,
          );

          if (showOnlineReversal) {
            return (
              <RefundDialog
                key={`refund-dialog-online-${info.row.original._id}`}
                id={info.row.original._id}
                amount={onlineAmount.toFixed(2)}
                mode={onlineMode}
                invoiceNumber={invoiceLabel}
              />
            );
          }

          if (manualAmount > 0) {
            // Manual-only: prompt staff to use the ledger expand.
            return (
              <div className='px-4 py-2 text-xs text-muted-foreground'>
                Expand ledger to reverse cash/check
              </div>
            );
          }

          return (
            <RefundDialog
              key={`refund-dialog-${info.row.original._id}`}
              id={info.row.original._id}
              amount={info.row.original.paidAmount}
              mode='stripe'
              invoiceNumber={invoiceLabel}
            />
          );
        },
      }),
    ],
    [
      formatDateForDisplay,
      formatDueDateForDisplay,
      currencyDetails?.symbol,
      hasAdminAccess,
    ],
  );

  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });
  const [date, setDate] = useState<DateRange | undefined>(
    getDefaultTransactionHistoryDateRange,
  );

  const startDate = date?.from
    ? format(date.from, 'yyyy-MM-dd')
    : format(subMonths(new Date(), 3), 'yyyy-MM-dd');
  const endDate = date?.to
    ? format(date.to, 'yyyy-MM-dd')
    : format(new Date(), 'yyyy-MM-dd');
  const [showCancelledInvoices, setShowCancelledInvoices] = useState(false);
  const [showVoidInvoices, setShowVoidInvoices] = useState(false);
  const [dateField, setDateField] = useState<StudentPaymentHistoryDateField>(
    STUDENT_PAYMENT_HISTORY_DATE_FIELD.ALL,
  );
  const [sortOrder, setSortOrder] = useState<PaymentHistorySortOrder>('desc');

  const { data, isFetching } = useGetStudentPaymentHistory({
    studentId,
    startDate,
    endDate,
    dateField,
    sortOrder,
    ...(showCancelledInvoices || showVoidInvoices
      ? {
          status: showCancelledInvoices
            ? showVoidInvoices
              ? 'cancelled,void'
              : 'cancelled'
            : showVoidInvoices
              ? 'void'
              : undefined,
        }
      : {}),
  });

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [
    studentId,
    dateField,
    sortOrder,
    date?.from,
    date?.to,
    showCancelledInvoices,
    showVoidInvoices,
  ]);

  // Sort data to show 'invoice scheduled' first, then 'failed', then the rest.
  // Credit-memo rows are ledger artifacts — reverse actions live on the source
  // invoice's Manual payment ledger, so hide them from the main table.
  const sortedData = useMemo(() => {
    if (!data) return [];
    return [...data]
      .filter((row) => !row.metadata?.creditMemo)
      .sort((a, b) => {
        if (a.status === 'invoice scheduled') return -1;
        if (b.status === 'invoice scheduled') return 1;
        if (a.status === 'failed') return -1;
        if (b.status === 'failed') return 1;
        if (a.status === 'pending') return -1;
        if (b.status === 'pending') return 1;
        return 0;
      });
  }, [data]);

  const renderManualPaymentDetails = useCallback(
    (row: Row<StudentPaymentHistoryInvoice>) => {
      const invoice = row.original;
      const manualPayments = getManualPayments(invoice.payments);
      if (manualPayments.length === 0) {
        return null;
      }

      const refundAction = resolvePaymentHistoryRefundAction(invoice);
      const manualAmount =
        refundAction.type === 'refund' ? refundAction.manualAmount : 0;
      const showManualReversal = hasAdminAccess && manualAmount > 0;
      const invoiceLabel = getInvoiceLabel(
        invoice.invoiceId,
        invoice.className,
      );

      return (
        <div className='border-t bg-muted/20 px-8 py-4'>
          <div className='mb-3 flex flex-wrap items-center justify-between gap-2'>
            <div className='flex items-center gap-2'>
              <div className='text-sm font-medium'>Manual payment ledger</div>
              <Badge variant='outline' className='text-xs'>
                {manualPayments.length}{' '}
                {manualPayments.length === 1 ? 'payment' : 'payments'}
              </Badge>
            </div>
            {showManualReversal ? (
              <RefundDialog
                id={invoice._id}
                amount={manualAmount.toFixed(2)}
                mode='manual'
                invoiceNumber={invoiceLabel}
              />
            ) : null}
          </div>
          <div className='rounded-lg border bg-background shadow-sm'>
            <div className='grid grid-cols-[120px_90px_100px_180px_220px] border-b bg-muted/40 px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              <div>Payment Date</div>
              <div>Method</div>
              <div>Amount</div>
              <div>Note / Reference</div>
              <div>Recorded By</div>
            </div>
            {manualPayments.map((payment, index) => {
              const isReversedLeg =
                Boolean(payment.isReversal) ||
                Boolean(payment.reversedAt) ||
                Number(payment.amount) < 0;
              return (
                <div
                  key={`${payment.method}-${payment.date}-${payment.amount}-${index}`}
                  className='grid grid-cols-[120px_90px_100px_180px_220px] items-center border-b px-4 py-3 text-sm last:border-b-0'
                >
                  <div>{formatDateForDisplay(payment.date, 'MMM d, yyyy')}</div>
                  <div className='flex flex-wrap items-center gap-1'>
                    <Badge variant='secondary'>
                      {formatPaymentMethodLabel(payment.method)}
                    </Badge>
                    {isReversedLeg ? (
                      <Badge variant='outline' className='text-xs'>
                        Reversed
                      </Badge>
                    ) : null}
                  </div>
                  <div
                    className={cn(
                      'font-medium',
                      Number(payment.amount) < 0 && 'text-red-600',
                    )}
                  >
                    {currencyDetails?.symbol}
                    {Number(payment.amount).toFixed(2)}
                  </div>
                  <div>
                    {payment.note?.trim()
                      ? payment.note.trim()
                      : (payment.checkNumber ?? '—')}
                  </div>
                  <div>
                    {payment.recordedByEmail ?? payment.recordedBy ?? '—'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    },
    [currencyDetails?.symbol, formatDateForDisplay, hasAdminAccess],
  );

  return (
    <EnrollioTable
      ariaLabel='Student transaction history'
      totalLabel='Transactions'
      filterHeader={
        <div className='flex flex-col gap-2 md:flex-row md:flex-wrap md:items-end'>
          <div className='grid gap-2'>
            <Label className='text-muted-foreground'>Date range</Label>
            <DateRangePicker date={date} setDate={setDate} />
          </div>
          <div className='flex min-w-0 flex-nowrap items-end gap-2'>
            <div className='grid min-w-0 flex-1 gap-2 md:flex-none'>
              <Label
                htmlFor='student-payment-history-date-field'
                className='text-muted-foreground'
              >
                Filter range by
              </Label>
              <Select
                value={dateField}
                onValueChange={(value) => {
                  setDateField(value as StudentPaymentHistoryDateField);
                }}
              >
                <SelectTrigger
                  id='student-payment-history-date-field'
                  className='w-full md:w-40'
                  aria-label='Date filter type'
                >
                  <SelectValue placeholder='Date type' />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value={STUDENT_PAYMENT_HISTORY_DATE_FIELD.ALL}>
                      All
                    </SelectItem>
                    <SelectItem value={STUDENT_PAYMENT_HISTORY_DATE_FIELD.DUE}>
                      Due date
                    </SelectItem>
                    <SelectItem value={STUDENT_PAYMENT_HISTORY_DATE_FIELD.PAID}>
                      Paid date
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className='grid min-w-0 flex-1 gap-2 md:flex-none'>
              <Label
                htmlFor='student-payment-history-sort-order'
                className='text-muted-foreground'
              >
                Sort order
              </Label>
              <Select
                value={sortOrder}
                onValueChange={(value) => {
                  setSortOrder(value as PaymentHistorySortOrder);
                }}
              >
                <SelectTrigger
                  id='student-payment-history-sort-order'
                  className='w-full md:w-36'
                  aria-label='Sort order'
                >
                  <SelectValue placeholder='Order' />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value='desc'>Descending</SelectItem>
                    <SelectItem value='asc'>Ascending</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' className='shrink-0'>
                  <EllipsisVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onSelect={() => {
                    setShowCancelledInvoices(!showCancelledInvoices);
                  }}
                >
                  {showCancelledInvoices
                    ? 'Hide cancelled invoices'
                    : 'Show cancelled invoices'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    setShowVoidInvoices(!showVoidInvoices);
                  }}
                >
                  {showVoidInvoices
                    ? 'Hide void invoices'
                    : 'Show void invoices'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      }
      toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
      columnFilters={columnFilters}
      columnVisibility={columnVisibility}
      columns={columns}
      data={sortedData}
      enableRowSelection={true}
      expanded={expanded}
      getRowId={(row) => row._id}
      onColumnFiltersChange={setColumnFilters}
      onExpandedChange={setExpanded}
      onColumnVisibilityChange={setColumnVisibility}
      onPaginationChange={setPagination}
      onRowSelectionChange={setRowSelection}
      onSortingChange={setSorting}
      pagination={pagination}
      isUpdating={isFetching}
      expandedRow={renderManualPaymentDetails}
      rowSelection={rowSelection}
      sorting={sorting}
      showSelectionCount
    />
  );
};
